import { Toaster } from "@/components/ui/toaster";
import { lazy, Suspense, useEffect } from "react";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { useAuth } from "@/lib/clerkShim";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { Loader2 } from "lucide-react";
import { setTokenProvider } from "@/lib/apiClient";
import { SignalRProvider } from "@/contexts/SignalRContext";
import { queryClient } from "@/lib/queryClient";

// Eager imports — critical path only
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import NotFound from "./pages/NotFound";

// Lazy-loaded public pages
const Demo = lazy(() => import("./pages/Demo"));
const Report = lazy(() => import("./pages/Report"));
const SampleReport = lazy(() => import("./pages/SampleReport"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const Services = lazy(() => import("./pages/Services"));
const ForFounders = lazy(() => import("./pages/ForFounders"));
const FounderAbout = lazy(() => import("./pages/founder/FounderAbout"));
const FounderContact = lazy(() => import("./pages/founder/FounderContact"));
const ICShareablePage = lazy(() => import("./pages/ICShareablePage"));
const SharedFile = lazy(() => import("./pages/SharedFile"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const Cookies = lazy(() => import("./pages/Cookies"));

// Lazy-loaded authenticated pages
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Profile = lazy(() => import("./pages/Profile"));
const NotificationCenter = lazy(() => import("./pages/NotificationCenter"));

// Lazy-loaded seller pages
const SellerDashboard = lazy(() => import("./pages/seller/SellerDashboard"));
const SellerEngagements = lazy(() => import("./pages/seller/SellerEngagements"));
const SellerEngagement = lazy(() => import("./pages/seller/SellerEngagement"));
const SellerUpload = lazy(() => import("./pages/seller/SellerUpload"));
const SellerQA = lazy(() => import("./pages/seller/SellerQA"));

// Lazy-loaded admin pages
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const EngagementsList = lazy(() => import("./pages/admin/EngagementsList"));
const NewEngagement = lazy(() => import("./pages/admin/NewEngagement"));
const EngagementDetail = lazy(() => import("./pages/admin/EngagementDetail"));
const RunAnalysis = lazy(() => import("./pages/admin/RunAnalysis"));
const AnalysisReview = lazy(() => import("./pages/admin/AnalysisReview"));
const ReviewsQueue = lazy(() => import("./pages/admin/ReviewsQueue"));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings"));
const BulkImport = lazy(() => import("./pages/admin/BulkImport"));
const UserManagement = lazy(() => import("./pages/admin/UserManagement"));
const SecurityDashboard = lazy(() => import("./pages/admin/SecurityDashboard"));
const AuditLogs = lazy(() => import("./pages/admin/AuditLogs"));
const DataRoom = lazy(() => import("./pages/admin/DataRoom"));
const FindingTracker = lazy(() => import("./pages/admin/FindingTracker"));
const Organizations = lazy(() => import("./pages/admin/Organizations"));
const QAManagement = lazy(() => import("./pages/admin/QAManagement"));
const RunRetest = lazy(() => import("./pages/admin/RunRetest"));
const RetestQueue = lazy(() => import("./pages/admin/RetestQueue"));
const ExecutiveDashboard = lazy(() => import("./pages/admin/ExecutiveDashboard"));
const CEOManagement = lazy(() => import("./pages/admin/CEOManagement"));
const ScheduledScansManagement = lazy(() => import("./pages/admin/ScheduledScansManagement"));
const Diagnostics = lazy(() => import("./pages/admin/Diagnostics"));
const RateLimitConfig = lazy(() => import("./pages/admin/RateLimitConfig"));

// Lazy-loaded buyer pages
const BuyerDashboard = lazy(() => import("./pages/buyer/BuyerDashboard"));
const BuyerReports = lazy(() => import("./pages/buyer/BuyerReports"));
const BuyerReport = lazy(() => import("./pages/buyer/BuyerReport"));
const RequestRetest = lazy(() => import("./pages/buyer/RequestRetest"));
const BuyerDataRoom = lazy(() => import("./pages/buyer/BuyerDataRoom"));

// Lazy-loaded CEO pages
const CEODashboard = lazy(() => import("./pages/ceo/CEODashboard"));
const CEOReports = lazy(() => import("./pages/ceo/CEOReports"));
const CEOReportDetail = lazy(() => import("./pages/ceo/CEOReportDetail"));
const CEOSettings = lazy(() => import("./pages/ceo/CEOSettings"));
const CEOBilling = lazy(() => import("./pages/ceo/CEOBilling"));
const CEOTeam = lazy(() => import("./pages/ceo/CEOTeam"));
const CEORepositories = lazy(() => import("./pages/ceo/CEORepositories"));
const CEOSampleReportPublic = lazy(() => import("./pages/ceo/CEOSampleReportPublic"));
const ReportExport = lazy(() => import("./pages/ReportExport"));

const LoadingFallback = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <Loader2 className="w-8 h-8 animate-spin text-primary" />
  </div>
);

/**
 * Registers the Clerk getToken function with apiClient
 * so all API calls include the JWT automatically.
 */
function TokenProviderSetup() {
  const { getToken } = useAuth();
  useEffect(() => {
    setTokenProvider(() => getToken());
  }, [getToken]);
  return null;
}

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <TokenProviderSetup />
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <SignalRProvider>
        <BrowserRouter>
          <Suspense fallback={<LoadingFallback />}>
            <Routes>
                {/* Public routes */}
                <Route path="/" element={<Index />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/demo" element={<Demo />} />
                <Route path="/report/:id" element={<Report />} />
                <Route path="/sample-report" element={<SampleReport />} />
                <Route path="/about" element={<About />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/services" element={<Services />} />
                <Route path="/report/:id/ic-summary" element={<ICShareablePage />} />
                <Route path="/report/:id/export" element={<ProtectedRoute allowedRoles={['internal_admin', 'internal_reviewer']}><ReportExport /></ProtectedRoute>} />
                <Route path="/for-founders" element={<ForFounders />} />
                <Route path="/for-founders/about" element={<FounderAbout />} />
                <Route path="/for-founders/contact" element={<FounderContact />} />
                <Route path="/shared/:token" element={<SharedFile />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/cookies" element={<Cookies />} />


                {/* Authenticated routes (any role) */}
                <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
                <Route path="/notifications" element={<ProtectedRoute><NotificationCenter /></ProtectedRoute>} />

                {/* Seller routes */}
                <Route path="/seller" element={<ProtectedRoute allowedRoles={['seller']}><SellerDashboard /></ProtectedRoute>} />
                <Route path="/seller/engagements" element={<ProtectedRoute allowedRoles={['seller']}><SellerEngagements /></ProtectedRoute>} />
                <Route path="/seller/engagements/:id" element={<ProtectedRoute allowedRoles={['seller']}><SellerEngagement /></ProtectedRoute>} />
                <Route path="/seller/upload" element={<ProtectedRoute allowedRoles={['seller']}><SellerUpload /></ProtectedRoute>} />
                <Route path="/seller/qa" element={<ProtectedRoute allowedRoles={['seller']}><SellerQA /></ProtectedRoute>} />

                {/* Admin/Internal routes */}
                <Route path="/admin" element={<ProtectedRoute allowedRoles={['internal_admin', 'internal_reviewer']}><AdminDashboard /></ProtectedRoute>} />
                <Route path="/admin/engagements" element={<ProtectedRoute allowedRoles={['internal_admin', 'internal_reviewer']}><EngagementsList /></ProtectedRoute>} />
                <Route path="/admin/engagements/new" element={<ProtectedRoute allowedRoles={['internal_admin', 'internal_reviewer']}><NewEngagement /></ProtectedRoute>} />
                <Route path="/admin/engagements/:id" element={<ProtectedRoute allowedRoles={['internal_admin', 'internal_reviewer']}><EngagementDetail /></ProtectedRoute>} />
                <Route path="/admin/engagements/:id/analysis/new" element={<ProtectedRoute allowedRoles={['internal_admin', 'internal_reviewer']}><RunAnalysis /></ProtectedRoute>} />
                <Route path="/admin/analysis/:id" element={<ProtectedRoute allowedRoles={['internal_admin', 'internal_reviewer']}><AnalysisReview /></ProtectedRoute>} />
                <Route path="/admin/reviews" element={<ProtectedRoute allowedRoles={['internal_admin', 'internal_reviewer']}><ReviewsQueue /></ProtectedRoute>} />
                <Route path="/admin/settings" element={<ProtectedRoute allowedRoles={['internal_admin']}><AdminSettings /></ProtectedRoute>} />
                <Route path="/admin/bulk-import" element={<ProtectedRoute allowedRoles={['internal_admin', 'internal_reviewer']}><BulkImport /></ProtectedRoute>} />
                <Route path="/admin/users" element={<ProtectedRoute allowedRoles={['internal_admin']}><UserManagement /></ProtectedRoute>} />
                <Route path="/admin/security" element={<ProtectedRoute allowedRoles={['internal_admin', 'internal_reviewer']}><SecurityDashboard /></ProtectedRoute>} />
                <Route path="/admin/audit-logs" element={<ProtectedRoute allowedRoles={['internal_admin']}><AuditLogs /></ProtectedRoute>} />
                <Route path="/admin/engagements/:id/data-room" element={<ProtectedRoute allowedRoles={['internal_admin', 'internal_reviewer']}><DataRoom /></ProtectedRoute>} />
                <Route path="/admin/findings" element={<ProtectedRoute allowedRoles={['internal_admin', 'internal_reviewer']}><FindingTracker /></ProtectedRoute>} />
                <Route path="/admin/analysis/:id/retest" element={<ProtectedRoute allowedRoles={['internal_admin', 'internal_reviewer']}><RunRetest /></ProtectedRoute>} />
                <Route path="/admin/retest-queue" element={<ProtectedRoute allowedRoles={['internal_admin', 'internal_reviewer']}><RetestQueue /></ProtectedRoute>} />
                <Route path="/admin/organizations" element={<ProtectedRoute allowedRoles={['internal_admin']}><Organizations /></ProtectedRoute>} />
                <Route path="/admin/qa" element={<ProtectedRoute allowedRoles={['internal_admin', 'internal_reviewer']}><QAManagement /></ProtectedRoute>} />
                <Route path="/admin/executive" element={<ProtectedRoute allowedRoles={['internal_admin']}><ExecutiveDashboard /></ProtectedRoute>} />
                <Route path="/admin/ceo-management" element={<ProtectedRoute allowedRoles={['internal_admin']}><CEOManagement /></ProtectedRoute>} />
                <Route path="/admin/scheduled-scans" element={<ProtectedRoute allowedRoles={['internal_admin', 'internal_reviewer']}><ScheduledScansManagement /></ProtectedRoute>} />
                <Route path="/admin/rate-limits" element={<ProtectedRoute allowedRoles={['internal_admin']}><RateLimitConfig /></ProtectedRoute>} />
                <Route path="/diagnostics" element={<ProtectedRoute allowedRoles={['internal_admin']}><Diagnostics /></ProtectedRoute>} />

                {/* Buyer routes */}
                <Route path="/buyer" element={<ProtectedRoute allowedRoles={['buyer']}><BuyerDashboard /></ProtectedRoute>} />
                <Route path="/buyer/reports" element={<ProtectedRoute allowedRoles={['buyer']}><BuyerReports /></ProtectedRoute>} />
                <Route path="/buyer/report/:id" element={<ProtectedRoute allowedRoles={['buyer']}><BuyerReport /></ProtectedRoute>} />
                <Route path="/buyer/report/:id/retest" element={<ProtectedRoute allowedRoles={['buyer']}><RequestRetest /></ProtectedRoute>} />
                <Route path="/buyer/data-room/:id" element={<ProtectedRoute allowedRoles={['buyer']}><BuyerDataRoom /></ProtectedRoute>} />

                {/* CEO routes */}
                <Route path="/ceo" element={<ProtectedRoute allowedRoles={['ceo']}><CEODashboard /></ProtectedRoute>} />
                <Route path="/ceo/reports" element={<ProtectedRoute allowedRoles={['ceo']}><CEOReports /></ProtectedRoute>} />
                <Route path="/ceo/reports/:id" element={<ProtectedRoute allowedRoles={['ceo']}><CEOReportDetail /></ProtectedRoute>} />
                <Route path="/ceo/settings" element={<ProtectedRoute allowedRoles={['ceo']}><CEOSettings /></ProtectedRoute>} />
                <Route path="/ceo/billing" element={<ProtectedRoute allowedRoles={['ceo']}><CEOBilling /></ProtectedRoute>} />
                <Route path="/ceo/team" element={<ProtectedRoute allowedRoles={['ceo']}><CEOTeam /></ProtectedRoute>} />
                <Route path="/ceo/repositories" element={<ProtectedRoute allowedRoles={['ceo']}><CEORepositories /></ProtectedRoute>} />
                <Route path="/ceo/sample-report" element={<CEOSampleReportPublic />} />

                {/* Catch-all */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </SignalRProvider>
        </TooltipProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
